import { useSyncExternalStore } from 'react';
import { LAUNCHER_CHANNELS, type LauncherChannel } from './useLauncherReadiness';

/**
 * Single source of truth for the launcher release channel (Stable / Dev),
 * shared across every surface that launches MineZero: the topbar
 * `LauncherButton` (which renders the picker) and the per-row Launch button on
 * TraineePortal cards (which has no picker of its own).
 *
 * Precedence: `VITE_MINEZERO_CHANNEL` (a hard, deployment-level override) wins
 * when set; otherwise the value is the trainee's own selection, persisted in
 * localStorage so it survives reloads and stays in sync across tabs.
 *
 * Implemented as a tiny module-level store (not a context) so any component can
 * read/update it without a provider, matching the repo's other localStorage
 * preferences (`mz_view`, tweaks).
 */

const STORAGE_KEY = 'mz_launcher_channel';

function normalize(value: string | null | undefined): LauncherChannel | null {
  return (LAUNCHER_CHANNELS as readonly string[]).includes(value ?? '')
    ? (value as LauncherChannel)
    : null;
}

/** Deployment override: when set, the picker is locked to this channel. */
const envChannel = normalize(import.meta.env.VITE_MINEZERO_CHANNEL);

function readStored(): LauncherChannel {
  if (envChannel) return envChannel;
  try {
    return normalize(localStorage.getItem(STORAGE_KEY)) ?? 'Stable';
  } catch {
    return 'Stable';
  }
}

let current: LauncherChannel = readStored();
const listeners = new Set<() => void>();

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  // Keep other tabs in sync when the selection changes.
  const onStorage = (e: StorageEvent) => {
    if (e.key !== STORAGE_KEY || envChannel) return;
    const next = normalize(e.newValue);
    if (next && next !== current) {
      current = next;
      cb();
    }
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(cb);
    window.removeEventListener('storage', onStorage);
  };
}

/** Update the shared channel. No-op when an env override is forcing the value. */
export function setLauncherChannel(next: LauncherChannel): void {
  if (envChannel || next === current) return;
  current = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Persistence is best-effort; the in-memory value still drives the UI.
  }
  listeners.forEach((l) => l());
}

/**
 * Read the shared launcher channel.
 *  - `channel`: the effective channel (env override or the user's selection).
 *  - `setChannel`: updates the selection (ignored when env-forced).
 *  - `envForced`: true when `VITE_MINEZERO_CHANNEL` is pinning the channel, so
 *    callers can disable the picker and explain why.
 */
export function useLauncherChannel(): {
  channel: LauncherChannel;
  setChannel: (next: LauncherChannel) => void;
  envForced: boolean;
} {
  const channel = useSyncExternalStore(
    subscribe,
    () => current,
    () => current,
  );
  return { channel, setChannel: setLauncherChannel, envForced: Boolean(envChannel) };
}
